import { useEffect, useMemo, useState } from 'react';
import { api, type AccountSessionSummary } from '../../api';
import { useI18n } from '../../i18n';
import { localizedError } from '../../i18n/errors';

export function SessionCard() {
  const { locale } = useI18n();
  const [sessions, setSessions] = useState<AccountSessionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const otherCount = useMemo(() => sessions.filter(item => !item.current).length, [sessions]);

  async function refresh() {
    const result = await api.accountSessions();
    setSessions(result.sessions);
  }

  useEffect(() => {
    setLoading(true); setError('');
    refresh().catch(err => setError(localizedError(err, locale, {
      'zh-CN': '加载登录会话失败。', en: 'Failed to load sign-in sessions.'
    }))).finally(() => setLoading(false));
  }, [locale]);

  function date(value: string) {
    return new Date(value).toLocaleString(locale === 'zh-CN' ? 'zh-CN' : 'en-US');
  }

  async function revoke(session: AccountSessionSummary) {
    if (busyId || session.current) return;
    const confirmed = window.confirm(locale === 'zh-CN'
      ? `结束 ${session.deviceLabel} 的登录会话？该设备下次操作时需要重新登录。`
      : `End the ${session.deviceLabel} session? That device will need to sign in again.`);
    if (!confirmed) return;
    setBusyId(session.id); setError(''); setNotice('');
    try {
      await api.revokeAccountSession(session.id);
      await refresh();
      setNotice(locale === 'zh-CN' ? '该登录会话已结束。' : 'The session was ended.');
    } catch (err) {
      setError(localizedError(err, locale, {
        'zh-CN': '结束会话失败。', en: 'Failed to end the session.'
      }));
    } finally { setBusyId(null); }
  }

  async function revokeOthers() {
    if (busyId || otherCount === 0) return;
    const confirmed = window.confirm(locale === 'zh-CN'
      ? `退出其他 ${otherCount} 个登录会话？当前设备会保持登录。`
      : `Sign out the other ${otherCount} session${otherCount === 1 ? '' : 's'}? This device will stay signed in.`);
    if (!confirmed) return;
    setBusyId('others'); setError(''); setNotice('');
    try {
      const result = await api.revokeOtherAccountSessions();
      await refresh();
      setNotice(locale === 'zh-CN' ? `已结束 ${result.revokedCount} 个其他会话。` : `Ended ${result.revokedCount} other session${result.revokedCount === 1 ? '' : 's'}.`);
    } catch (err) {
      setError(localizedError(err, locale, {
        'zh-CN': '退出其他会话失败。', en: 'Failed to sign out other sessions.'
      }));
    } finally { setBusyId(null); }
  }

  return <section className="account-card account-sessions-card">
    <div className="account-card-heading">
      <div><span className="eyebrow">Sessions</span><h3>{locale === 'zh-CN' ? '登录设备与会话' : 'Devices and sessions'}</h3><p>{locale === 'zh-CN' ? '这里只保存粗粒度设备类型，不保存原始 User-Agent 或 IP。' : 'Only a coarse device label is stored here; raw User-Agent and IP are not retained for this view.'}</p></div>
      <button className="text-button" type="button" disabled={Boolean(busyId) || otherCount === 0} onClick={() => { revokeOthers().catch(console.error); }}>{busyId === 'others' ? (locale === 'zh-CN' ? '处理中…' : 'Working…') : (locale === 'zh-CN' ? '退出其他设备' : 'Sign out others')}</button>
    </div>
    {error && <p className="form-error">{error}</p>}
    {notice && <p className="form-success">{notice}</p>}
    {loading ? <p className="field-help">{locale === 'zh-CN' ? '正在读取会话…' : 'Loading sessions…'}</p> : <div className="session-list">
      {sessions.map(session => <article className="session-row" key={session.id}>
        <div><strong>{session.deviceLabel}</strong><span>{session.current ? (locale === 'zh-CN' ? '当前设备' : 'Current device') : (locale === 'zh-CN' ? '其他设备' : 'Other device')}</span></div>
        <small>{locale === 'zh-CN' ? '最近活动' : 'Last active'} · {date(session.lastSeenAt)}<br />{locale === 'zh-CN' ? '登录于' : 'Signed in'} · {date(session.createdAt)}</small>
        {session.current
          ? <span className="status-pill">{locale === 'zh-CN' ? '当前' : 'Current'}</span>
          : <button className="text-button danger-text" type="button" disabled={Boolean(busyId)} onClick={() => { revoke(session).catch(console.error); }}>{busyId === session.id ? (locale === 'zh-CN' ? '处理中…' : 'Working…') : (locale === 'zh-CN' ? '结束会话' : 'End session')}</button>}
      </article>)}
      {!sessions.length && <p className="empty-state">{locale === 'zh-CN' ? '当前没有有效登录会话。' : 'There are no active sign-in sessions.'}</p>}
    </div>}
  </section>;
}
